import styled, { keyframes } from "styled-components";

const ringPulse = keyframes`
  0% {
    opacity: 0.2;
    stroke-width: 1.4;
  }
  50% {
    opacity: 0.72;
    stroke-width: 2.4;
  }
  100% {
    opacity: 0.2;
    stroke-width: 1.4;
  }
`;

export const PitchTrackerFrame = styled.div`
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 0 80px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.06);
  background: ${(props) => props.theme.pitchTrackerBg};
`;

export const PitchTrackerSvg = styled.svg`
  display: block;
  background: repeating-linear-gradient(
    ${(props) => (props.$grassStripeDirection === "horizontal" ? "0deg" : "90deg")},
    ${(props) => props.theme.pitchTrackerDark} 0px,
    ${(props) => props.theme.pitchTrackerDark} 50px,
    ${(props) => props.theme.pitchTrackerLight} 50px,
    ${(props) => props.theme.pitchTrackerLight} 100px
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: ${(props) => (props.$drawing ? "crosshair" : "auto")};

  .pitch-line {
    fill: none;
    stroke: ${(props) => props.theme.pitchTrackerLine};
    stroke-width: 1.5;
    pointer-events: none;
  }

  .pitch-spot {
    fill: ${(props) => props.theme.pitchTrackerLine};
    pointer-events: none;
  }

  .event-dot-group {
    cursor: pointer;
  }

  .event-dot-core {
    transition: r 0.15s ease, filter 0.15s ease;
    stroke: rgba(8, 10, 14, 0.9);
    stroke-width: 1.5;
    vector-effect: non-scaling-stroke;
  }

  .event-dot-hover-ring {
    opacity: 0;
    fill: none;
    stroke-width: 1.65;
    transform-origin: center;
    transition: opacity 0.2s ease;
    pointer-events: none;
  }

  .event-dot-group:hover .event-dot-core,
  .event-dot-group.is-hovered .event-dot-core {
    r: 8.15;
    filter: brightness(1.08);
  }

  .event-dot-group:hover .event-dot-hover-ring,
  .event-dot-group.is-hovered .event-dot-hover-ring {
    opacity: 1;
    animation: ${ringPulse} 1.1s ease-in-out infinite;
  }
`;

export const HoverTrackerContainer = styled.div`
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transform: ${(props) =>
    props.$visible ? "translateY(0) scale(1)" : "translateY(6px) scale(0.97)"};
  transition: opacity 0.18s ease, transform 0.18s ease;
`;

export const HoverTrackerInner = styled.div`
  min-width: 220px;
  max-width: 280px;
  padding: 14px 16px;
  border-radius: 12px;
  backdrop-filter: blur(20px) saturate(1.4);
  -webkit-backdrop-filter: blur(20px) saturate(1.4);
  background: ${(props) => props.theme.pitchTrackerGlassBg};
  border: 1px solid ${(props) => props.theme.pitchTrackerGlassBorder};
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6);
`;

export const HoverTrackerPlayer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

export const HoverTrackerAvatar = styled.div`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid ${(props) => props.theme.pitchTrackerGlassBorder};
  background: linear-gradient(135deg, #333, #555);
  color: ${(props) => props.theme.pitchTrackerText};
  font-family: "SatoshiBold";
  font-size: 12px;

  img {
    width: 100%;
    height: 100%;
    max-width: none;
    display: block;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    object-position: center;
  }
`;

export const HoverTrackerName = styled.div`
  color: ${(props) => props.theme.pitchTrackerText};
  font-family: "SatoshiBold";
  font-size: 13px;
  line-height: 1.3;
`;

export const HoverTrackerCompetition = styled.div`
  margin-top: 2px;
  color: ${(props) => props.theme.pitchTrackerMuted};
  font-family: "SatoshiRegular";
  font-size: 10px;
  letter-spacing: 0.5px;
  line-height: 1.3;
`;

export const HoverTrackerBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  padding: 4px 10px;
  margin-bottom: 6px;
  font-family: "SatoshiBold";
  font-size: 12px;
  letter-spacing: 0.7px;
`;

export const HoverTrackerRecipient = styled.div`
  color: ${(props) => props.theme.pitchTrackerMuted};
  font-family: "SatoshiRegular";
  font-size: 11px;
  line-height: 1.3;

  span {
    color: ${(props) => props.theme.pitchTrackerText};
    font-family: "SatoshiMedium";
  }
`;

export const HoverTrackerHint = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${(props) => props.theme.pitchTrackerGlassBorder};
  color: ${(props) => props.theme.pitchTrackerAccent};
  font-family: "SatoshiMedium";
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
`;
